import type { ActionPlanEntry } from "../../utils/rules";

export interface ActionTrackedChange {
    author: string;
    bookmark_name: string;
    date: string;
    id: string;
    tag: string;
    text: string;
}

export type ActionPlanStepFilter = (step: any) => boolean;

export interface ValidActionsAndSteps {
    validActions: ActionPlanEntry[];
    validStepsMap: Map<ActionPlanEntry, any[]>;
}

export const hasRedlineStepContent = (step: any): boolean => {
    if (!step?.action_report) return false;

    if (step.action_type === "comment" || step.action_type === "comment_once") {
        return false;
    }

    const report = step.action_report;
    const carriesOoxml = Array.isArray(report.target_ooxml_after) && report.target_ooxml_after.length > 0;
    const carriesTexts = report.target_texts !== undefined;
    const carriesBookmarks = Array.isArray(report.target_bookmarks) && report.target_bookmarks.length > 0;
    return carriesOoxml || carriesTexts || carriesBookmarks;
};

export const hasCommentStepContent = (step: any): boolean => {
    if (!step?.action_report) return false;

    if (step.action_type !== "comment" && step.action_type !== "comment_once") {
        return false;
    }

    const report = step.action_report;
    return (
        Array.isArray(report.action_tracked_changes) &&
        report.action_tracked_changes.length > 0 &&
        report.action_tracked_changes.some((change: any) => change.tag === "comment")
    );
};

export const getValidActionsAndSteps = (actionPlan: ActionPlanEntry[], stepFilter: ActionPlanStepFilter): ValidActionsAndSteps => {
    const getValidSteps = (action: any, stepFilter: ActionPlanStepFilter): any[] => {
        if (!action?.steps) return [];
        return action.steps.filter(stepFilter);
    };
    if (!actionPlan) return { validActions: [], validStepsMap: new Map() };

    const validActions = actionPlan.filter(action => getValidSteps(action, stepFilter).length > 0);
    const validStepsMap = new Map<ActionPlanEntry, any[]>(
        validActions.map(action => [
            action,
            getValidSteps(action, stepFilter)
        ])
    );

    return { validActions, validStepsMap };
};

export const getValidRedlineActionsAndSteps = (actionPlan: ActionPlanEntry[]): ValidActionsAndSteps =>
    getValidActionsAndSteps(actionPlan, hasRedlineStepContent);

export const getValidCommentActionsAndSteps = (actionPlan: ActionPlanEntry[]): ValidActionsAndSteps =>
    getValidActionsAndSteps(actionPlan, hasCommentStepContent);

export const getOriginalActionIndex = (validActionIndex: number, validActions: ActionPlanEntry[], actionPlan: ActionPlanEntry[]): number => {
    if (validActionIndex < 0 || validActionIndex >= validActions.length) return -1;
    const validAction = validActions[validActionIndex];
    return actionPlan.findIndex(action => action === validAction);
};

export const getOriginalStepIndex = (validStepIndex: number, validSteps: any[], originalAction: ActionPlanEntry): number => {
    if (validStepIndex < 0 || validStepIndex >= validSteps.length) return -1;
    const validStep = validSteps[validStepIndex];
    return originalAction.steps.findIndex(step => step === validStep);
};
